import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { t as translate } from "../i18n/translations.js";

const API_BASE = "/api";

const DEFAULTS = {
  primaryColor: "#6366f1",
  secondaryColor: "#8b5cf6",
  accentColor: "#10b981",
  backgroundColor: "#f8f7ff",
  textColor: "#1a1a1a",
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  borderRadius: "20",
  buttonStyle: "gradient",
};

function b(branding) { return { ...DEFAULTS, ...branding }; }

function btnStyle(br, override) {
  const r = parseInt(br.borderRadius || "20");
  const base = {
    display: "block", width: "100%", padding: "16px 24px", fontSize: 16,
    fontWeight: 700, borderRadius: r, cursor: "pointer", textAlign: "center",
    textDecoration: "none", boxSizing: "border-box", fontFamily: br.fontFamily,
    WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease",
  };
  if (br.buttonStyle === "outline")
    return { ...base, background: "transparent", color: br.primaryColor, border: `2px solid ${br.primaryColor}`, ...override };
  if (br.buttonStyle === "solid")
    return { ...base, background: br.primaryColor, color: "#fff", border: "none", ...override };
  return { ...base, background: `linear-gradient(135deg, ${br.primaryColor}, ${br.secondaryColor})`, color: "#fff", border: "none", ...override };
}

export default function CustomerLandingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [sizeResult, setSizeResult] = useState(null);
  const [sizeLoading, setSizeLoading] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const tt = (key, params) => translate(data?.language || "nl", key, params);

  useEffect(() => {
    if (!token) { setError("landing.invalidLink"); setLoading(false); return; }
    fetch(`${API_BASE}/fitting-session/landing/${encodeURIComponent(token)}`)
      .then((r) => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then(setData)
      .catch(() => setError("landing.notFound"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSizeCalc = async () => {
    if (!age || !height || !weight) return;
    setSizeLoading(true);
    try {
      const res = await fetch(`${API_BASE}/fitting-session/size-calculator`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ age: Number(age), height: Number(height), weight: Number(weight), productId: data.product.id, shopDomain: data.session.shopDomain || "" }),
      });
      setSizeResult(await res.json());
    } catch { setSizeResult({ size: "?", advice: tt("landing.calcFailed") }); }
    finally { setSizeLoading(false); }
  };

  // --- Loading ---
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100dvh", background: DEFAULTS.backgroundColor }}>
        <style>{CSS_ANIMATIONS}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${DEFAULTS.primaryColor}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: "#666", fontSize: 14, fontFamily: DEFAULTS.fontFamily }}>{tt("common.loading")}</p>
        </div>
      </div>
    );
  }

  // --- Error ---
  if (error || !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100dvh", background: DEFAULTS.backgroundColor, fontFamily: DEFAULTS.fontFamily }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <h2 style={{ margin: "0 0 8px", fontWeight: 700 }}>{tt("landing.pageNotFound")}</h2>
          <p style={{ color: "#666" }}>{error ? tt(error) : tt("common.error")}</p>
        </div>
      </div>
    );
  }

  const { session, product, landingPage, productUrl, language: pageLang, branding: rawBranding } = data;
  const br = b(rawBranding || {});
  const radius = parseInt(br.borderRadius);
  const pageTitle = landingPage.title || tt("landing.defaultTitle", { product: product.title });
  const introText = landingPage.introText || tt("landing.defaultIntro");
  const additionalImages = (() => { try { return JSON.parse(landingPage.additionalImages || "[]"); } catch { return []; } })();
  const shopUrl = session.shopDomain ? `https://${session.shopDomain}` : null;
  const orderUrl = landingPage.orderButtonUrl || productUrl || shopUrl;

  const card = { background: "#fff", borderRadius: radius, marginBottom: 20, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.06)", animation: "fadeUp 0.5s ease both" };
  const cardInner = { padding: "20px 24px" };
  const label = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: br.primaryColor, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 };

  return (
    <div style={{ minHeight: "100dvh", background: br.backgroundColor, fontFamily: br.fontFamily, color: br.textColor }}>
      <style>{CSS_ANIMATIONS}</style>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 16px 100px" }}>
        {/* Hero header */}
        <div style={{ textAlign: "center", padding: "32px 0 24px", animation: "fadeUp 0.4s ease both" }}>
          {br.logoUrl && <img src={br.logoUrl} alt="" style={{ maxHeight: 48, marginBottom: 12 }} />}
          {br.storeName && !br.logoUrl && (
            <div style={{ fontSize: 13, fontWeight: 600, color: br.primaryColor, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>{br.storeName}</div>
          )}
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.25, letterSpacing: -0.3 }}>{pageTitle}</h1>
          <p style={{ fontSize: 15, color: `${br.textColor}99`, margin: "10px auto 0", lineHeight: 1.6, maxWidth: 480 }}>{introText}</p>
        </div>

        {/* AI Try-On Hero */}
        {session.resultImageB64 && (
          <div style={{ ...card, animationDelay: "0.1s" }}>
            <div style={cardInner}><div style={label}>{tt("landing.yourLook")}</div></div>
            <div style={{ background: `linear-gradient(135deg, ${br.primaryColor}08, ${br.secondaryColor}08)` }}>
              <img
                src={`data:${session.resultImageB64.startsWith("/9j/") ? "image/jpeg" : "image/png"};base64,${session.resultImageB64}`}
                alt={tt("landing.yourLook")}
                onLoad={() => setImgLoaded(true)}
                style={{ width: "100%", display: "block", maxHeight: 700, objectFit: "contain", opacity: imgLoaded ? 1 : 0, transition: "opacity 0.5s ease" }}
              />
            </div>
          </div>
        )}

        {/* Style Advice */}
        {session.resultDescription && (
          <div style={{ ...card, animationDelay: "0.2s" }}>
            <div style={cardInner}>
              <div style={label}>{tt("landing.styleAdvice")}</div>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: `${br.textColor}cc`, whiteSpace: "pre-line", margin: 0 }}>{session.resultDescription}</p>
            </div>
          </div>
        )}

        {/* Recommended sizes */}
        {(session.recommendedSizes || session.recommendedSize) && (() => {
          let sizes = null;
          try { sizes = typeof session.recommendedSizes === "string" ? JSON.parse(session.recommendedSizes) : session.recommendedSizes; } catch {}
          const hasSizes = sizes && typeof sizes === "object" && Object.keys(sizes).length > 0;
          const garmentLabels = { colbert: tt("landing.colbert"), gilet: tt("landing.gilet"), pantalon: tt("landing.pantalon"), overhemd: tt("landing.overhemd") };
          let advice = null;
          try { advice = typeof session.sizeAdvice === "string" ? JSON.parse(session.sizeAdvice) : session.sizeAdvice; } catch {}
          const alternatives = advice?.alternatives || null;
          const adjustments = advice?.adjustments || null;
          const fitNotes = advice?.fitNotes || null;
          const tipKeyMap = {
            athletic_jacket: "tryOn.fitTipAthleticJacket",
            athletic_jacket_option: "tryOn.fitTipAthleticJacketOption",
            fuller_jacket: "tryOn.fitTipFullerJacket",
            between_jacket: "tryOn.fitTipBetweenJacket",
            between_trousers: "tryOn.fitTipBetweenTrousers",
            trousers_alter: "tryOn.fitTipTrousersAlter",
            between_gilet: "tryOn.fitTipBetweenGilet",
          };
          const fitTipLines = fitNotes?.garments ? Object.entries(fitNotes.garments).map(([g, n]) => {
            const key = tipKeyMap[n.tip];
            if (!key) return null;
            return tt(key, { size: sizes?.[g] || "", alt: n.alt || "" });
          }).filter(Boolean) : [];
          const buildLine = fitNotes?.build === "athletic" ? tt("tryOn.fitBuildAthletic", { drop: fitNotes.drop })
            : fitNotes?.build === "fuller" ? tt("tryOn.fitBuildFuller", { drop: fitNotes.drop }) : null;
          const adjustmentLines = adjustments ? [...new Set(Object.values(adjustments).flatMap(a =>
            (a.applied || []).map(adj =>
              adj.type === "customer_history" ? tt("tryOn.adjustedByHistory")
              : adj.type === "vendor_bias" ? tt(adj.direction === "up" ? "tryOn.adjustedByBrandUp" : "tryOn.adjustedByBrandDown")
              : adj.type === "fit_preference" ? tt("tryOn.adjustedByFit")
              : null
            ).filter(Boolean)
          ))] : [];
          return (
            <div style={{ ...card, animationDelay: "0.25s" }}>
              <div style={cardInner}>
                <div style={label}>👔 {hasSizes ? tt("landing.recommendedSizes") : tt("landing.recommendedSize")}</div>
                {hasSizes ? (
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(Object.keys(sizes).length, 3)}, 1fr)`, gap: 12 }}>
                    {Object.entries(sizes).map(([type, size]) => (
                      <div key={type} style={{ background: `${br.primaryColor}08`, borderRadius: radius * 0.6, padding: "16px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: `${br.textColor}88`, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>
                          {garmentLabels[type] || type}
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: br.primaryColor }}>{size}</div>
                        {alternatives?.[type] && (
                          <div style={{ fontSize: 11, color: `${br.textColor}77`, marginTop: 4 }}>
                            {tt("tryOn.alternativeSizeHint", { alt: alternatives[type] })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ background: `${br.primaryColor}08`, borderRadius: radius * 0.6, padding: 20, textAlign: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: `${br.textColor}88`, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>
                      {tt("landing.overall")}
                    </div>
                    <div style={{ fontSize: 36, fontWeight: 800, color: br.primaryColor }}>{session.recommendedSize}</div>
                  </div>
                )}
                {adjustmentLines.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    {adjustmentLines.map((line, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, color: `${br.textColor}aa`, padding: "3px 0", lineHeight: 1.5 }}>
                        <span style={{ color: br.accentColor || "#10b981", flexShrink: 0 }}>✓</span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(buildLine || fitTipLines.length > 0) && (
                  <div style={{ marginTop: 14, background: `${br.primaryColor}06`, borderRadius: radius * 0.5, padding: "12px 14px" }}>
                    {buildLine && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: br.primaryColor, marginBottom: fitTipLines.length ? 8 : 0, textTransform: "uppercase", letterSpacing: 0.6 }}>
                        💪 {buildLine}
                      </div>
                    )}
                    {fitTipLines.map((line, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, color: `${br.textColor}bb`, padding: "3px 0", lineHeight: 1.55 }}>
                        <span style={{ flexShrink: 0 }}>✂️</span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Body measurements */}
        {session.bodyMeasurements && (() => {
          let bm = null;
          try { bm = typeof session.bodyMeasurements === "string" ? JSON.parse(session.bodyMeasurements) : session.bodyMeasurements; } catch {}
          if (!bm || typeof bm !== "object") return null;
          const measurementKeys = [
            "chest", "waist", "hips", "shoulders", "armLength", "inseam", "neck",
            "backLength", "frontLength", "outseam", "thigh", "knee", "frontRise", "backRise",
          ];
          const measurementLabels = {
            chest: tt("landing.chest"), waist: tt("landing.waist"), hips: tt("landing.hips"),
            shoulders: tt("landing.shoulders"), armLength: tt("landing.armLength"),
            inseam: tt("landing.inseam"), neck: tt("landing.neck"),
            backLength: tt("landing.backLength") || "Ruglengte",
            frontLength: tt("landing.frontLength") || "Voorlengte",
            outseam: tt("landing.outseam") || "Buitenbeenlengte",
            thigh: tt("landing.thigh") || "Dij",
            knee: tt("landing.knee") || "Knie",
            frontRise: tt("landing.frontRise") || "Voorhoogte",
            backRise: tt("landing.backRise") || "Achterhoogte",
          };
          const available = measurementKeys.filter(k => bm[k]);
          if (available.length === 0) return null;
          const confKey = bm.confidence ? `landing.confidence${bm.confidence.charAt(0).toUpperCase() + bm.confidence.slice(1)}` : null;
          return (
            <div style={{ ...card, animationDelay: "0.28s" }}>
              <div style={cardInner}>
                <div style={label}>📏 {tt("landing.bodyMeasurements")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 10 }}>
                  {available.map(key => (
                    <div key={key} style={{ background: `${br.accentColor}08`, borderRadius: radius * 0.5, padding: "12px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: `${br.textColor}77`, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {measurementLabels[key] || tt(`landing.${key}`)}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: br.textColor }}>
                        {bm[key]} <span style={{ fontSize: 12, color: `${br.textColor}88` }}>{tt("landing.cm")}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {confKey && (
                  <div style={{ marginTop: 12, fontSize: 12, color: `${br.textColor}77`, textAlign: "right" }}>
                    {tt("landing.confidence")}: {tt(confKey)}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Original photos */}
        <div style={{ ...card, animationDelay: "0.3s" }}>
          <div style={cardInner}>
            <div style={label}>{session.resultImageB64 ? tt("landing.originalPhotos") : tt("landing.yourPhotoAndArticle")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {session.customerPhoto && <img src={`data:image/jpeg;base64,${session.customerPhoto}`} alt={tt("landing.yourPhoto")} style={{ width: "100%", borderRadius: radius * 0.6, display: "block" }} />}
              {(product.garmentImageUrl || product.imageUrl) && <img src={product.garmentImageUrl || product.imageUrl} alt={product.title} style={{ width: "100%", borderRadius: radius * 0.6, display: "block" }} />}
            </div>
          </div>
        </div>

        {/* Size guide */}
        {landingPage.sizeGuideText && (
          <div style={{ ...card, animationDelay: "0.35s" }}>
            <div style={cardInner}>
              <div style={label}>{tt("landing.sizeAdvice")}</div>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: `${br.textColor}cc`, whiteSpace: "pre-line", margin: 0 }}>{landingPage.sizeGuideText}</p>
            </div>
          </div>
        )}

        {/* Size calculator */}
        {landingPage.showSizeCalculator && (
          <div style={{ ...card, animationDelay: "0.4s" }}>
            <div style={cardInner}>
              <div style={label}>{tt("landing.sizeCalculator")}</div>
              <p style={{ fontSize: 13, color: `${br.textColor}80`, margin: "0 0 14px" }}>{tt("landing.sizeCalcHelp")}</p>
              <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                {[{ val: age, set: setAge, ph: tt("landing.age") }, { val: height, set: setHeight, ph: tt("landing.heightCm") }, { val: weight, set: setWeight, ph: tt("landing.weightKg") }].map(({ val, set, ph }) => (
                  <input key={ph} type="number" placeholder={ph} value={val} onChange={(e) => set(e.target.value)} inputMode="numeric"
                    style={{ width: "100%", padding: "13px 14px", fontSize: 15, borderRadius: radius * 0.5, border: `1.5px solid ${br.primaryColor}25`, outline: "none", boxSizing: "border-box", fontFamily: br.fontFamily, transition: "border-color 0.2s" }}
                    onFocus={(e) => e.target.style.borderColor = br.primaryColor} onBlur={(e) => e.target.style.borderColor = `${br.primaryColor}25`} />
                ))}
              </div>
              <button style={{ ...btnStyle(br), width: "100%", marginTop: 12, opacity: sizeLoading ? 0.6 : 1, background: `${br.primaryColor}12`, color: br.primaryColor, border: "none", fontSize: 15 }} onClick={handleSizeCalc} disabled={sizeLoading || !age || !height || !weight}>
                {sizeLoading ? tt("landing.calculating") : tt("landing.calculateMySize")}
              </button>
              {sizeResult && (
                <div style={{ background: `${br.primaryColor}08`, borderRadius: radius * 0.6, padding: 20, marginTop: 14, textAlign: "center" }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color: br.primaryColor }}>{tt("landing.sizeResult", { size: sizeResult.size })}</div>
                  {sizeResult.advice && <p style={{ fontSize: 14, color: `${br.textColor}88`, margin: "8px 0 0" }}>{sizeResult.advice}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional text */}
        {landingPage.additionalText && (
          <div style={{ ...card, animationDelay: "0.45s" }}>
            <div style={cardInner}>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: `${br.textColor}cc`, whiteSpace: "pre-line", margin: 0 }}>{landingPage.additionalText}</p>
            </div>
          </div>
        )}

        {/* Additional images */}
        {additionalImages.length > 0 && (
          <div style={{ ...card, animationDelay: "0.5s" }}>
            <div style={cardInner}>
              <div style={label}>{tt("landing.extraPhotos")}</div>
              <div style={{ display: "grid", gridTemplateColumns: additionalImages.length === 1 ? "1fr" : "1fr 1fr", gap: 10 }}>
                {additionalImages.map((img, i) => <img key={i} src={img} alt={`Extra ${i + 1}`} style={{ width: "100%", borderRadius: radius * 0.6, display: "block" }} />)}
              </div>
            </div>
          </div>
        )}

        {/* Inline CTA */}
        {orderUrl && (
          <a href={orderUrl} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle(br), marginBottom: 20, animation: "fadeUp 0.5s ease both", animationDelay: "0.55s" }}>
            {tt("landing.orderOutfit")}
          </a>
        )}

        {/* Account CTA */}
        <div style={{ ...card, background: `linear-gradient(135deg, ${br.accentColor}08, ${br.accentColor}15)`, animationDelay: "0.6s" }}>
          <div style={{ ...cardInner, textAlign: "center" }}>
            <div style={{ ...label, justifyContent: "center" }}>{tt("landing.dashboardPromo")}</div>
            <p style={{ fontSize: 14, color: `${br.textColor}88`, margin: "0 0 16px", lineHeight: 1.6 }}>
              {tt("landing.dashboardPromoText")}
            </p>
            <a href="/my-outfits" style={{ ...btnStyle(br, { background: `linear-gradient(135deg, ${br.accentColor}, ${br.accentColor}cc)` }), display: "inline-block", width: "auto", padding: "14px 32px", fontSize: 15 }}>
              {tt("landing.viewOutfits")}
            </a>
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "28px 0 16px", color: `${br.textColor}40`, fontSize: 12 }}>{tt("common.poweredBy")}</div>
      </div>

      {/* Floating order button */}
      {orderUrl && (
        <a href={orderUrl} target="_blank" rel="noopener noreferrer"
          style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", width: "calc(100% - 32px)", maxWidth: 608, ...btnStyle(br, { padding: "18px 24px", fontSize: 17 }), boxShadow: `0 4px 24px ${br.primaryColor}40, 0 2px 8px rgba(0,0,0,0.12)`, zIndex: 1000 }}>
          {tt("landing.orderOutfit")}
        </a>
      )}
    </div>
  );
}

const CSS_ANIMATIONS = `
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:wght@700&family=Poppins:wght@400;600;700&family=Montserrat:wght@400;600;700&display=swap');
`;
